/**
 * Feature 073 — Inline edit panel for an existing document's metadata.
 * Mounted by ProviderDocumentsManager when a row's Edit button is clicked.
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import type { OwnerProviderDocument } from '../../types/provider-profile.types';
import type { UpdateProviderDocumentInput } from '../../api/providerDocumentsApi';
import {
  MAX_DESCRIPTION,
  MAX_DISPLAY_NAME,
} from './ProviderDocumentUploadForm';

type ProviderDocumentEditDialogProps = {
  doc: OwnerProviderDocument;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (input: UpdateProviderDocumentInput) => Promise<void>;
};

export function ProviderDocumentEditDialog({
  doc,
  isSaving,
  onCancel,
  onSave,
}: ProviderDocumentEditDialogProps) {
  const [displayName, setDisplayName] = useState(doc.displayName);
  const [description, setDescription] = useState(doc.description ?? '');

  const handleSave = async () => {
    await onSave({
      displayName: displayName.trim().slice(0, MAX_DISPLAY_NAME),
      description: description.trim().slice(0, MAX_DESCRIPTION) || null,
    });
  };

  return (
    <div
      role="dialog"
      aria-label="Edit document"
      className="rounded-lg border border-border bg-card p-4"
    >
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-foreground">Edit document</h4>
        <button
          type="button"
          aria-label="Cancel edit"
          onClick={onCancel}
          className="rounded-md p-1 hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 space-y-3">
        <input
          type="text"
          value={displayName}
          maxLength={MAX_DISPLAY_NAME}
          aria-label="Display name"
          onChange={(e) => setDisplayName(e.target.value)}
          className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <input
          type="text"
          value={description}
          maxLength={MAX_DESCRIPTION}
          aria-label="Description"
          onChange={(e) => setDescription(e.target.value)}
          className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
